import { type HTMLAttributes, memo, type ReactNode, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Spacing16, Spacing24, Spacing28, Spacing40 } from 'autogen/design-tokens/tokens';
import {
    CustomListItemDiscriminator,
    type ListItem,
    ListItemDiscriminator,
    type ListRowItem,
    type ProductListItemModel
} from 'autogen/swagger/all';
import FullwidthConnectedContent from 'components/connectedContent/fullwidthConnectedContent';
import InlineConnectedContent from 'components/connectedContent/inlineConnectedContent';
import ProductItem from 'components/shared/productItem';
import useIntersectionObserver from 'hooks/globals/useIntersectionObserver';
import { breakpoint, sizes } from 'variables';
import { useSetIsProductListIntersecting } from './shared/hooks/useIsProductListIntersecting';
import type { ThemeType } from './shared/types';

/* #region styles */
const Grid = styled.div`
    display: grid;
    column-gap: ${Spacing16};
    row-gap: ${Spacing28};
    grid-template-columns: repeat(2, calc(50% - ${Spacing16} / 2));
    grid-auto-flow: dense;
    width: 100%;

    ${breakpoint.up(sizes.md)} {
        column-gap: ${Spacing24};
        row-gap: ${Spacing40};
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    }
`;

/* #endregion */
type IListItem = ListItem | ListRowItem | ProductListItemModel;

interface IProductList extends HTMLAttributes<HTMLDivElement> {
    items: IListItem[];
    themeType?: ThemeType;
}

// Number of items to eager load images for
const eagerLoadCount = 2;

export function ProductListItemRenderer(props: IProductList) {
    const { items, themeType } = props;

    function getListRowItem({ item }, index) {
        return CustomListItemDiscriminator<ReactNode>(
            item,
            (model) => <FullwidthConnectedContent key={`fullwidth_${model.location}_${index}`} model={model} />,
            (model) => <InlineConnectedContent key={`inline_${model.placement}`} model={model} />,
            () => null, // Cannot be rendered in list, intended for inventory lane
            () => null, // cannot be rendered in list, intended for Menu
            () => null, // Cannot be rendered in list, intended for pdp
            () => {
                throw Error('Api mismatch - Productlist');
            }
        );
    }

    function getListItem({ item }, index) {
        return CustomListItemDiscriminator<ReactNode>(
            item,
            (model) => <FullwidthConnectedContent key={`fullwidth_${model.location}_${index}`} model={model} />,
            (model) => <InlineConnectedContent key={`inline_${model.placement}`} model={model} />,
            () => null, // Cannot be rendered in list, intended for inventory lane
            () => null, // cannot be rendered in list, intended for Menu
            () => null, // Cannot be rendered in list, intended for pdp
            () => {
                throw Error('Api mismatch - Productlist');
            }
        );
    }

    function getProductListItems(inputModel, index) {
        return ListItemDiscriminator<ReactNode>(
            inputModel,
            (model) => getListItem(model, index),
            (model) => getListRowItem(model, index),
            (model) => (
                <ProductItem
                    key={model.id}
                    model={model}
                    eagerLoad={index < eagerLoadCount}
                    themeType={themeType}
                    pageContext="ProductList"
                />
            ),
            () => {
                throw Error('Api mismatch - Productlist');
            }
        );
    }

    return <>{items.map((model, index) => getProductListItems(model, index))}</>;
}

const ProductList = (props: IProductList) => {
    const { items, themeType, ...restProps } = props;
    const ref = useRef<HTMLDivElement>(null);
    const entry = useIntersectionObserver(ref, {});
    const setIsProductListIntersecting = useSetIsProductListIntersecting();

    useEffect(() => {
        setIsProductListIntersecting(!!entry?.isIntersecting);
    }, [entry, setIsProductListIntersecting]);

    return (
        <Grid ref={ref} {...restProps}>
            <ProductListItemRenderer items={items} themeType={themeType} />
        </Grid>
    );
};

export default memo(ProductList);
